import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { makeApiCall } from '../../config/api';

const TimeTrackingReport = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Fetch employees list
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await makeApiCall('api/time-tracking/admin/employees-list');
        if (response.ok) {
          const result = await response.json();
          setEmployees(result.employees || []);
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };
    fetchEmployees();

    // Auto-load last 30 days
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    setFromDate(from);
    setToDate(to);
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());
      
      if (selectedEmployees.length > 0) {
        const empIds = selectedEmployees.map(e => e.employee_id).join(',');
        params.append('employee_ids', empIds);
      }

      const response = await makeApiCall(`api/reports/time-tracking?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching time tracking report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const empIds = selectedEmployees.length > 0 
        ? selectedEmployees.map(e => e.employee_id).join(',') 
        : null;

      const response = await makeApiCall('api/reports/time-tracking/pdf', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
          employee_ids: empIds
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `time-tracking-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, []); // Initial load

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value ? new Date(e.target.value) : null);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Group data by employee
  const groupedData = {};
  data.forEach(entry => {
    const empId = entry.employee_id || entry.user_id;
    if (!groupedData[empId]) {
      groupedData[empId] = {
        name: entry.employee_name || `${entry.first_name} ${entry.last_name}` || entry.username,
        department: entry.department_name,
        entries: [],
        total_hours: 0
      };
    }
    groupedData[empId].entries.push(entry);
    if (entry.hours_worked) {
      groupedData[empId].total_hours += parseFloat(entry.hours_worked);
    }
  });

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(option) => option.employee_name || `${option.first_name} ${option.last_name}`}
              value={selectedEmployees}
              onChange={(event, newValue) => setSelectedEmployees(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('reports.employee')} 
                  placeholder={t('reports.selectEmployee')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchReport}
                disabled={loading || !fromDate || !toDate}
                sx={{ bgcolor: '#1976d2' }}
              >
                {t('reports.load')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={downloadPDF}
                disabled={generatingPDF || data.length === 0}
                sx={{ bgcolor: '#d32f2f' }}
              >
                {t('reports.downloadPDF')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      {data.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('reports.summary')}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={`${t('reports.totalJobs')}: ${data.length}`} color="primary" />
              <Chip
                label={`${t('reports.uniqueCustomers')}: ${Object.keys(groupedData).length}`} // Actually unique employees here
                color="secondary"
              />
              <Chip
                 label={`${t('reports.timeTracking.totalHours')}: ${formatDuration(data.reduce((sum, e) => sum + parseFloat(e.hours_worked || 0), 0))}`}
                 color="success"
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Report Data */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('reports.selectDateRange')}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {Object.values(groupedData).map((employee, idx) => (
            <Card key={idx} sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#474747' }}>
                    {employee.name} {employee.department ? `(${employee.department})` : ''}
                  </Typography>
                  <Chip 
                    label={`${t('reports.timeTracking.totalHours')}: ${formatDuration(employee.total_hours)}`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><strong>{t('reports.date')}</strong></TableCell>
                        <TableCell><strong>{t('reports.timeTracking.clockIn')}</strong></TableCell>
                        <TableCell><strong>{t('reports.timeTracking.clockOut')}</strong></TableCell>
                        <TableCell><strong>{t('reports.timeTracking.duration')}</strong></TableCell>
                        <TableCell><strong>{t('reports.timeTracking.project')}</strong></TableCell>
                        <TableCell><strong>{t('reports.status')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employee.entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{new Date(entry.clock_in_time).toLocaleDateString()}</TableCell>
                          <TableCell>{new Date(entry.clock_in_time).toLocaleTimeString()}</TableCell>
                          <TableCell>{entry.clock_out_time ? new Date(entry.clock_out_time).toLocaleTimeString() : '-'}</TableCell>
                          <TableCell>{formatDuration(entry.hours_worked)}</TableCell>
                          <TableCell>
                            {entry.project_code || ''} {entry.cost_center ? `(${entry.cost_center})` : ''}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.status}
                              size="small"
                              color={entry.status === 'Completed' ? 'success' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TimeTrackingReport;

